// File: src/pages/targets/CustomerTarget.jsx

import React, { useEffect, useMemo, useState } from "react";
import axios from "../../import/axiosConfig";
import Swal from "sweetalert2";
import {
  PurchaseProductList_Product,
  View_User_By_Id2,
} from "../../import/ApiEndPoint/Api";
import { CreateCustomerList } from "../../import/ApiEndPoint/ApiCalling";

const API = {
  list: "/target/yearly-target/list",
  update: "/target/yearly-target/update",
};

const FILTER_ALL = "__ALL__";
const LS_PREFIX = "CUSTOMER_TARGET_LOCKED_SPLIT_V2";

const FY_MONTHS = [
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
  "January",
  "February",
  "March",
];

const CALENDAR_MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const arr = (v) => (Array.isArray(v) ? v : []);
const str = (v) => (v === null || v === undefined ? "" : String(v));
const norm = (v) => str(v).trim().toLowerCase();

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const r2 = (v) => Number(num(v).toFixed(2));
const money = (v) => `₹ ${Number(num(v)).toFixed(2)}`;
const getUser = () => JSON.parse(localStorage.getItem("userData") || "{}");

const first = (...values) => {
  for (const value of values) {
    if (value !== undefined && value !== null && String(value).trim() !== "") {
      return value;
    }
  }

  return "";
};

const safeReadLS = (key, fallback) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
};

const safeWriteLS = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // ignore
  }
};

const safeRemoveLS = (key) => {
  try {
    localStorage.removeItem(key);
  } catch {
    // ignore
  }
};

const targetsCacheKey = (db) => `${LS_PREFIX}:targets:${db || "NO_DB"}`;
const peopleCacheKey = (db) => `${LS_PREFIX}:people:${db || "NO_DB"}`;
const productsCacheKey = (db) => `${LS_PREFIX}:products:${db || "NO_DB"}`;

const monthDraftKey = (db, fy, customerId, month) =>
  `${LS_PREFIX}:month-draft:${db || "NO_DB"}:${fy}:${customerId}:${month}`;

const autoSplitDoneKey = (db, fy, targetId) =>
  `${LS_PREFIX}:auto-split-done:${db || "NO_DB"}:${fy}:${
    targetId || "NO_TARGET"
  }`;

const cleanName = (v) => {
  if (!v) return "";

  if (typeof v === "object") {
    return str(
      v.categoryName ||
        v.subCategoryName ||
        v.subcategoryName ||
        v.SubCategoryName ||
        v.groupName ||
        v.name ||
        v.title ||
        v.label ||
        v.value ||
        v.Product_Category ||
        v.Product_SubCategory ||
        "",
    ).trim();
  }

  return str(v).trim();
};

const getCurrentFY = () => {
  const d = new Date();
  const y = d.getFullYear();

  return d.getMonth() >= 3
    ? `${y}-${String((y + 1) % 100).padStart(2, "0")}`
    : `${y - 1}-${String(y % 100).padStart(2, "0")}`;
};

const getFYStartYear = (fyLabel) => {
  const n = Number(String(fyLabel || "").split("-")[0]);
  return Number.isFinite(n) ? n : 0;
};

const getCurrentFYMonthIndex = () => {
  const currentMonthName = CALENDAR_MONTHS[new Date().getMonth()];
  return FY_MONTHS.indexOf(currentMonthName);
};

const isMonthCompleted = (fyLabel, monthName) => {
  const targetFyStart = getFYStartYear(fyLabel);
  const currentFyStart = getFYStartYear(getCurrentFY());
  const monthIndex = FY_MONTHS.indexOf(str(monthName).trim());
  const currentMonthIndex = getCurrentFYMonthIndex();

  if (!targetFyStart || monthIndex < 0) return true;
  if (targetFyStart < currentFyStart) return true;
  if (targetFyStart > currentFyStart) return false;

  return monthIndex < currentMonthIndex;
};

const getFYOptions = () => {
  const start = Number(getCurrentFY().split("-")[0]);
  const list = [];

  for (let y = start - 3; y <= start + 3; y += 1) {
    list.push(`${y}-${String((y + 1) % 100).padStart(2, "0")}`);
  }

  return list.reverse();
};

const getProductId = (p = {}) =>
  str(p._id || p.id || p.productId || p.ProductId || p.sId || "");

const getProductName = (p = {}) =>
  str(
    p.Product_Title ||
      p.productName ||
      p.ProductName ||
      p.name ||
      p.title ||
      "Unnamed Product",
  );

const getProductCategory = (p = {}) =>
  cleanName(
    first(
      p.category,
      p.Category,
      p.catageory,
      p.Catageory,
      p.productCategory,
      p.Product_Category,
      p.categoryName,
      p.groupName,
    ),
  ) || "Uncategorized";

const getProductSubCategory = (p = {}) =>
  cleanName(
    first(
      p.subcategory,
      p.subCategory,
      p.SubCategory,
      p.sub_category,
      p.productSubCategory,
      p.Product_SubCategory,
      p.subCategoryName,
      p.subcategoryName,
    ),
  ) || "No Sub Category";

const getProductColor = (p = {}) =>
  cleanName(
    first(
      p.color,
      p.Color,
      p.colour,
      p.Colour,
      p.productColor,
      p.Product_Color,
      p.shade,
      p.Shade,
      p.variantColor,
      p.VariantColor,
    ),
  ) || "No Color";

const getProductRate = (p = {}) =>
  num(
    first(
      p.SalesRate,
      p.salesRate,
      p.SaleRate,
      p.saleRate,
      p.BasicPrice,
      p.basicPrice,
      p.price,
      p.Price,
      p.rate,
      p.Rate,
      p.Product_MRP,
    ),
  );

const getSecondarySize = (p = {}) => {
  const value = num(
    first(
      p.secondarySize,
      p.secondarysize,
      p.SecondarySize,
      p.secondaryUnitSize,
      p.SecondaryUnitSize,
      p.conversion,
      p.Conversion,
    ),
  );

  return value > 0 ? value : 1;
};

const getSecondaryUnitName = (p = {}) =>
  str(
    first(
      p.secondaryUnit,
      p.SecondaryUnit,
      p.secondaryUnitName,
      p.SecondaryUnitName,
      p.secUnit,
      p.unitSecondary,
    ),
  ) || "Secondary";

const getPrimaryUnitName = (p = {}) =>
  str(
    first(
      p.primaryUnit,
      p.PrimaryUnit,
      p.primaryUnitName,
      p.PrimaryUnitName,
      p.unit,
      p.Unit,
    ),
  ) || "Qty";

const getSavedProducts = (target = {}) =>
  arr(target.productTargets?.length ? target.productTargets : target.products);

const getMonthProducts = (monthRow = {}) =>
  arr(
    monthRow.productTargets?.length
      ? monthRow.productTargets
      : monthRow.products,
  );

const getSavedProductId = (product = {}) =>
  str(
    first(
      product.productId,
      product.ProductId,
      product._id,
      product.id,
      product.sId,
      product.productCode,
      product.Product_Code,
      product.productName,
    ),
  );

const makeOptionList = (rows, getter) => {
  const map = new Map();

  arr(rows).forEach((row) => {
    const label = getter(row);
    const key = norm(label);

    if (key && !map.has(key)) {
      map.set(key, label);
    }
  });

  return Array.from(map.values()).sort((a, b) =>
    String(a).localeCompare(String(b)),
  );
};

const makeFallbackMonth = (target, month, index) => {
  const products = getSavedProducts(target).map((row) => {
    const secondaryQty = r2(num(first(row.secondaryQty, row.sQty)) / 12);
    const primaryQty = r2(
      num(first(row.primaryQty, row.pQty, row.qty, row.qtyAssign)) / 12,
    );
    const total = r2(
      num(first(row.total, row.totalPrice, row.targetAmount)) / 12,
    );

    return {
      ...row,
      secondaryQty,
      sQty: secondaryQty,
      primaryQty,
      pQty: primaryQty,
      qty: primaryQty,
      qtyAssign: primaryQty,
      total,
      totalPrice: total,
      targetAmount: total,
    };
  });

  return {
    month,
    monthIndex: index,
    targetTotal: r2(num(target.grandTotal) / 12),
    totalSecondaryQty: r2(
      products.reduce((sum, p) => sum + num(first(p.secondaryQty, p.sQty)), 0),
    ),
    totalPrimaryQty: r2(
      products.reduce(
        (sum, p) => sum + num(first(p.primaryQty, p.pQty, p.qty)),
        0,
      ),
    ),
    productTargets: products,
    products,
    isEdited: false,
    isCustomerEdited: false,
  };
};

const getMonthlyTargets = (target = {}) => {
  const rows = arr(target.monthlyTargets);

  return FY_MONTHS.map((month, index) => {
    const found = rows.find((row) => norm(row.month) === norm(month));

    if (!found) return makeFallbackMonth(target, month, index);

    const products = getMonthProducts(found);

    return {
      ...found,
      month,
      monthIndex: index,
      targetTotal: r2(num(found.targetTotal)),
      totalSecondaryQty: r2(
        products.reduce(
          (sum, p) => sum + num(first(p.secondaryQty, p.sQty)),
          0,
        ),
      ),
      totalPrimaryQty: r2(
        products.reduce(
          (sum, p) => sum + num(first(p.primaryQty, p.pQty, p.qty)),
          0,
        ),
      ),
      productTargets: products,
      products,
      isEdited: Boolean(found.isEdited),
      isCustomerEdited: Boolean(found.isCustomerEdited),
    };
  });
};

const scaleTargetProduct = (product = {}, divisor = 1) => {
  const safeDivisor = num(divisor) > 0 ? num(divisor) : 1;

  const secondaryQty = r2(
    num(first(product.secondaryQty, product.sQty)) / safeDivisor,
  );

  const primaryQty = r2(
    num(
      first(product.primaryQty, product.pQty, product.qty, product.qtyAssign),
    ) / safeDivisor,
  );

  const total = r2(
    num(first(product.total, product.totalPrice, product.targetAmount)) /
      safeDivisor,
  );

  return {
    ...product,
    secondaryQty,
    sQty: secondaryQty,
    primaryQty,
    pQty: primaryQty,
    qty: primaryQty,
    qtyAssign: primaryQty,
    total,
    totalPrice: total,
    targetAmount: total,
  };
};

const makeCustomerMonthFromYearMonth = (monthRow = {}, divisor = 1) => {
  const products = getMonthProducts(monthRow).map((product) =>
    scaleTargetProduct(product, divisor),
  );

  const targetTotal = r2(
    products.reduce(
      (sum, product) =>
        sum +
        num(first(product.total, product.totalPrice, product.targetAmount)),
      0,
    ),
  );

  return {
    ...monthRow,
    targetTotal:
      targetTotal || r2(num(monthRow.targetTotal) / (num(divisor) || 1)),
    totalSecondaryQty: r2(
      products.reduce((sum, p) => sum + num(first(p.secondaryQty, p.sQty)), 0),
    ),
    totalPrimaryQty: r2(
      products.reduce(
        (sum, p) => sum + num(first(p.primaryQty, p.pQty, p.qty)),
        0,
      ),
    ),
    productTargets: products,
    products,
    isEdited: false,
    isCustomerEdited: false,
  };
};

const aggregateProductsFromMonths = (monthlyTargets = []) => {
  const map = new Map();

  arr(monthlyTargets).forEach((monthRow) => {
    getMonthProducts(monthRow).forEach((product) => {
      const key =
        getSavedProductId(product) || str(product.productName || product.name);

      if (!key) return;

      if (!map.has(key)) {
        map.set(key, {
          ...product,
          productId: str(
            first(product.productId, product._id, product.id, key),
          ),
          secondaryQty: 0,
          sQty: 0,
          primaryQty: 0,
          pQty: 0,
          qty: 0,
          qtyAssign: 0,
          total: 0,
          totalPrice: 0,
          targetAmount: 0,
        });
      }

      const row = map.get(key);

      row.secondaryQty += num(first(product.secondaryQty, product.sQty));
      row.sQty = row.secondaryQty;

      row.primaryQty += num(
        first(product.primaryQty, product.pQty, product.qty, product.qtyAssign),
      );
      row.pQty = row.primaryQty;
      row.qty = row.primaryQty;
      row.qtyAssign = row.primaryQty;

      row.total += num(
        first(product.total, product.totalPrice, product.targetAmount),
      );
      row.totalPrice = row.total;
      row.targetAmount = row.total;
    });
  });

  return Array.from(map.values()).map((product) => ({
    ...product,
    secondaryQty: r2(product.secondaryQty),
    sQty: r2(product.sQty),
    primaryQty: r2(product.primaryQty),
    pQty: r2(product.pQty),
    qty: r2(product.qty),
    qtyAssign: r2(product.qtyAssign),
    total: r2(product.total),
    totalPrice: r2(product.totalPrice),
    targetAmount: r2(product.targetAmount),
  }));
};

const productToRow = (product = {}, qtyMap = {}) => {
  const id = getProductId(product) || str(product.productId || product._id);
  const secondaryQty = num(qtyMap[id]);
  const secondarySize = getSecondarySize(product);
  const primaryQty = r2(secondaryQty * secondarySize);
  const saleRate = getProductRate(product);
  const total = r2(primaryQty * saleRate);

  return {
    id,
    product,
    productName: getProductName(product),
    category: getProductCategory(product),
    subCategory: getProductSubCategory(product),
    color: getProductColor(product),
    saleRate,
    secondaryQty,
    secondarySize,
    primaryQty,
    total,
    primaryUnitName: getPrimaryUnitName(product),
    secondaryUnitName: getSecondaryUnitName(product),
  };
};

const savedProductToProductMasterShape = (product = {}) => {
  const id = str(product.productId || product._id || product.id || "");

  return {
    _id: id,
    id,
    productId: id,
    Product_Title:
      product.productName ||
      product.Product_Title ||
      product.name ||
      "Unnamed Product",
    productName:
      product.productName ||
      product.Product_Title ||
      product.name ||
      "Unnamed Product",
    category: product.category || product.catageory || "Uncategorized",
    catageory: product.category || product.catageory || "Uncategorized",
    subCategory:
      product.subCategory || product.SubCategory || "No Sub Category",
    color: product.color || "No Color",
    shade: product.color || "No Color",
    saleRate: product.saleRate || product.basicPrice || product.price || 0,
    basicPrice: product.saleRate || product.basicPrice || product.price || 0,
    price: product.saleRate || product.basicPrice || product.price || 0,
    secondarySize: product.secondarySize || 1,
    secondaryUnitName: product.secondaryUnitName || "Secondary",
    primaryUnitName: product.primaryUnitName || "Qty",
  };
};

const makeProductPayload = (sourceRows) =>
  arr(sourceRows).map((row) => ({
    productId: row.id,
    productName: row.productName,
    category: row.category,
    catageory: row.category,
    subCategory: row.subCategory,
    color: row.color,

    basicPrice: Number(row.saleRate || 0),
    saleRate: Number(row.saleRate || 0),
    price: Number(row.saleRate || 0),

    secondaryUnitName: row.secondaryUnitName,
    primaryUnitName: row.primaryUnitName,
    secondarySize: Number(row.secondarySize || 1),

    secondaryQty: Number(row.secondaryQty || 0),
    sQty: Number(row.secondaryQty || 0),

    primaryQty: Number(row.primaryQty || 0),
    pQty: Number(row.primaryQty || 0),
    qty: Number(row.primaryQty || 0),
    qtyAssign: Number(row.primaryQty || 0),

    total: Number(row.total || 0),
    totalPrice: Number(row.total || 0),
    targetAmount: Number(row.total || 0),
  }));

const getPossibleIds = (row) => {
  const values = [
    row?._id,
    row?.id,
    row?.userId,
    row?.customerId,
    row?.partyId,
    row?.sId,
    row?.employeeId,
    row?.customerCode,
    row?.code,
  ];

  if (typeof row?.partyId === "object") {
    values.push(row?.partyId?._id, row?.partyId?.id);
  }

  if (typeof row?.userId === "object") {
    values.push(row?.userId?._id, row?.userId?.id);
  }

  return values
    .map(str)
    .map((x) => x.trim())
    .filter(Boolean);
};

const getMainId = (row) => getPossibleIds(row)[0] || "";

const getCustomerTargetMainId = (target = {}) =>
  str(
    first(
      target.customerId,
      target.customer_id,
      target.partyId,
      target.customer?._id,
      target.customer?.id,
      target.customerCode,
    ),
  );

const getDisplayName = (row, fallback = "User") =>
  row?.firstName ||
  row?.fullName ||
  row?.name ||
  row?.userName ||
  row?.CompanyName ||
  row?.companyName ||
  row?.ownerName ||
  row?.email ||
  row?.mobileNumber ||
  fallback;

const getCustomerName = (row) =>
  row?.CompanyName ||
  row?.companyName ||
  row?.ownerName ||
  row?.firstName ||
  row?.name ||
  "Customer";

const extractUserListFromResponse = (data) => {
  if (Array.isArray(data?.adminDetails)) return data.adminDetails;
  if (Array.isArray(data?.User)) return data.User;
  if (Array.isArray(data?.users)) return data.users;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.result)) return data.result;
  if (Array.isArray(data)) return data;
  return [];
};

const buildUserApiPath = (uid, db) => {
  if (typeof View_User_By_Id2 === "string" && View_User_By_Id2.trim()) {
    return `${View_User_By_Id2.replace(/\/+$/, "")}/${uid}/${db}`;
  }

  return `/user/view-user-by-id/${uid}/${db}`;
};

const getRolePosition = (role) => {
  const value =
    role?.rolePosition ??
    role?.position ??
    role?.rank ??
    role?.assign ??
    role?.rolename?.rolePosition ??
    role?.rolename?.position ??
    role?.rolename?.rank ??
    0;

  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const getRoleName = (role) =>
  role?.roleName ||
  role?.name ||
  role?.rolename?.roleName ||
  role?.rolename?.name ||
  role?.id ||
  "";

const getPersonRole = (person) =>
  person?.rolename || person?.roleName || person?.role || {};

const roleMatches = (person, role) => {
  if (!person || !role) return false;

  const personRole = getPersonRole(person);
  const personRoleId = norm(personRole?._id || personRole?.id);
  const roleId = norm(role?._id || role?.id);
  const personRoleName = norm(getRoleName(personRole));
  const roleName = norm(getRoleName(role));
  const personPosition = getRolePosition(personRole);
  const rolePosition = getRolePosition(role);

  if (personRoleId && roleId && personRoleId === roleId) return true;
  if (personRoleName && roleName && personRoleName === roleName) return true;
  if (personPosition && rolePosition && personPosition === rolePosition) {
    return true;
  }

  return false;
};

const isSalesPersonUser = (user = {}, salesPersonRole = null) => {
  if (salesPersonRole && roleMatches(user, salesPersonRole)) return true;

  const personRole = getPersonRole(user);
  const roleName = norm(getRoleName(personRole));
  const pos = getRolePosition(personRole);

  if (pos === 2) return true;
  if (roleName.includes("sales person")) return true;
  if (roleName.includes("salesperson")) return true;
  if (roleName.includes("sales executive")) return true;
  if (roleName.includes("sales officer")) return true;
  if (roleName.includes("sales representative")) return true;

  if (
    roleName.includes("sales") &&
    !roleName.includes("manager") &&
    !roleName.includes("head") &&
    !roleName.includes("admin")
  ) {
    return true;
  }

  return false;
};

const isSalesManagerUser = (user = {}, salesManagerRole = null) => {
  if (salesManagerRole && roleMatches(user, salesManagerRole)) return true;

  const personRole = getPersonRole(user);
  const roleName = norm(getRoleName(personRole));
  const pos = getRolePosition(personRole);

  if (pos === 1) return true;
  if (roleName.includes("sales manager")) return true;
  if (roleName.includes("manager") && roleName.includes("sales")) return true;

  return false;
};

const extractIdFromValue = (value) => {
  if (!value) return "";

  if (typeof value === "object") {
    return str(
      value?._id ||
        value?.userId ||
        value?.customerId ||
        value?.id ||
        value?.sId ||
        "",
    );
  }

  return str(value);
};

const getAssignedSalesPersonIds = (customer = {}) => {
  const fields = [
    customer?.created_by,
    customer?.createdBy,
    customer?.assigned_to,
    customer?.assignedTo,
    customer?.assignTo,
    customer?.assignedUser,
    customer?.assignedUsers,
    customer?.assignUser,
    customer?.assignUsers,
    customer?.salesPersonId,
    customer?.salespersonId,
    customer?.salesPerson,
    customer?.salesperson,
    customer?.selectedSalesPerson,
    customer?.selectedSalesperson,
    customer?.userId,
    customer?.user,
    customer?.parentId,
    customer?.parentUserId,
    customer?.parentUser,
    customer?.parent,
    customer?.reportingTo,
    customer?.reportingManager,
  ];

  const ids = [];

  fields.forEach((field) => {
    if (!field) return;

    if (Array.isArray(field)) {
      field.forEach((item) => {
        const id = extractIdFromValue(item);
        if (id) ids.push(id);
      });
      return;
    }

    const id = extractIdFromValue(field);
    if (id) ids.push(id);
  });

  return Array.from(new Set(ids.filter(Boolean)));
};

const getAssignedManagerIds = (salesPerson = {}) => {
  const fields = [
    salesPerson?.parent,
    salesPerson?.parentId,
    salesPerson?.parentUser,
    salesPerson?.parentUserId,
    salesPerson?.manager,
    salesPerson?.managerId,
    salesPerson?.managerUser,
    salesPerson?.managerUserId,
    salesPerson?.reportingTo,
    salesPerson?.reportingManager,
    salesPerson?.assigned_to,
    salesPerson?.assignedTo,
    salesPerson?.assignTo,
    salesPerson?.created_by,
    salesPerson?.createdBy,
  ];

  const ids = [];

  fields.forEach((field) => {
    if (!field) return;

    if (Array.isArray(field)) {
      field.forEach((item) => {
        const id = extractIdFromValue(item);
        if (id) ids.push(id);
      });
      return;
    }

    const id = extractIdFromValue(field);
    if (id) ids.push(id);
  });

  return Array.from(new Set(ids.filter(Boolean)));
};

const getAssignedSalesPersonName = (customer = {}, users = []) => {
  const ids = getAssignedSalesPersonIds(customer);

  const found = arr(users).find((user) => {
    const userIds = getPossibleIds(user);
    return ids.some((id) => userIds.includes(id));
  });

  if (found) return getDisplayName(found, "Sales Person");

  const direct =
    customer?.selectedSalesPerson ||
    customer?.selectedSalesperson ||
    customer?.salesPerson ||
    customer?.salesperson ||
    customer?.assigned_to ||
    customer?.assignedTo ||
    customer?.assignTo ||
    customer?.user ||
    customer?.created_by ||
    customer?.createdBy;

  if (typeof direct === "object") return getDisplayName(direct, "");

  return "";
};

const isCustomerAssignedToSalesPerson = (customer, salesPerson) => {
  const spIds = getPossibleIds(salesPerson);
  const assignedIds = getAssignedSalesPersonIds(customer);

  return assignedIds.some((id) => spIds.includes(id));
};

const isSalesPersonAssignedToManager = (salesPerson, manager) => {
  const managerIds = getPossibleIds(manager);
  const assignedManagerIds = getAssignedManagerIds(salesPerson);

  return assignedManagerIds.some((id) => managerIds.includes(id));
};

const isDebtorCustomer = (customer = {}) => {
  const status = norm(customer?.status);

  if (status && status !== "active") return false;

  const fields = [
    customer?.partyType,
    customer?.customerType,
    customer?.type,
    customer?.Type,
    customer?.accountType,
    customer?.nature,
    customer?.category?.groupName,
    customer?.registrationType,
    customer?.groupName,
    customer?.ledgerGroup,
    customer?.underGroup,
    customer?.accountGroup,
  ]
    .map(norm)
    .filter(Boolean);

  const blockWords = [
    "purchase",
    "purchase only",
    "supplier",
    "vendor",
    "creditor",
    "sundry creditor",
    "sundry creditors",
    "payable",
  ];

  if (
    fields.some((value) =>
      blockWords.some((word) => value === word || value.includes(word)),
    )
  ) {
    return false;
  }

  const debtorWords = [
    "debtor",
    "debtors",
    "sundry debtor",
    "sundry debtors",
    "receivable",
    "customer",
    "sale",
    "sales",
  ];

  if (
    fields.some((value) =>
      debtorWords.some((word) => value === word || value.includes(word)),
    )
  ) {
    return true;
  }

  return true;
};

const makeGeneratedCustomerTarget = (
  customer = {},
  yearlyTarget = {},
  customerCount = 1,
  users = [],
) => {
  const divisor = num(customerCount) > 0 ? num(customerCount) : 1;
  const monthlyTargets = getMonthlyTargets(yearlyTarget).map((monthRow) =>
    makeCustomerMonthFromYearMonth(monthRow, divisor),
  );

  const productTargets = aggregateProductsFromMonths(monthlyTargets);

  const yearlyTotal = r2(
    monthlyTargets.reduce(
      (sum, monthRow) => sum + num(monthRow.targetTotal),
      0,
    ),
  );

  return {
    customerId: getMainId(customer),
    customerIds: getPossibleIds(customer),
    customerName: getCustomerName(customer),
    salesPersonName:
      getAssignedSalesPersonName(customer, users) || "Unassigned",
    yearlyTotal,
    targetTotal: yearlyTotal,
    grandTotal: yearlyTotal,
    productTargets,
    products: productTargets,
    monthlyTargets,
    generated: true,
    isEdited: false,
    updatedAt: new Date().toISOString(),
  };
};

const makeEmptyCustomerTarget = (customer = {}, users = []) => {
  const monthlyTargets = FY_MONTHS.map((month, index) => ({
    month,
    monthIndex: index,
    targetTotal: 0,
    totalSecondaryQty: 0,
    totalPrimaryQty: 0,
    productTargets: [],
    products: [],
    isEdited: false,
    isCustomerEdited: false,
  }));

  return {
    customerId: getMainId(customer),
    customerIds: getPossibleIds(customer),
    customerName: getCustomerName(customer),
    salesPersonName:
      getAssignedSalesPersonName(customer, users) || "Unassigned",
    yearlyTotal: 0,
    targetTotal: 0,
    grandTotal: 0,
    productTargets: [],
    products: [],
    monthlyTargets,
    generated: true,
    isEdited: false,
    updatedAt: new Date().toISOString(),
  };
};

const findStoredCustomerTarget = (storedRows = [], customer = {}) => {
  const customerIds = getPossibleIds(customer);
  const customerName = norm(getCustomerName(customer));

  return (
    arr(storedRows).find((target) => {
      const storedId = getCustomerTargetMainId(target);

      if (storedId && customerIds.includes(storedId)) return true;

      const storedIds = arr(target.customerIds).map(str);
      if (storedIds.some((id) => customerIds.includes(id))) return true;

      if (customerName && norm(target.customerName) === customerName) {
        return true;
      }

      return false;
    }) || null
  );
};

const normalizeStoredCustomerTarget = (
  customer = {},
  storedTarget = {},
  users = [],
) => {
  const monthlyTargets = FY_MONTHS.map((month, index) => {
    const storedMonth = arr(storedTarget.monthlyTargets).find(
      (m) => norm(m.month) === norm(month),
    );

    if (!storedMonth) {
      return {
        month,
        monthIndex: index,
        targetTotal: 0,
        totalSecondaryQty: 0,
        totalPrimaryQty: 0,
        productTargets: [],
        products: [],
        isEdited: false,
        isCustomerEdited: false,
      };
    }

    const products = getMonthProducts(storedMonth);

    return {
      ...storedMonth,
      month,
      monthIndex: index,
      targetTotal: r2(num(storedMonth.targetTotal)),
      totalSecondaryQty: r2(
        products.reduce(
          (sum, p) => sum + num(first(p.secondaryQty, p.sQty)),
          0,
        ),
      ),
      totalPrimaryQty: r2(
        products.reduce(
          (sum, p) => sum + num(first(p.primaryQty, p.pQty, p.qty)),
          0,
        ),
      ),
      productTargets: products,
      products,
      isEdited: Boolean(storedMonth.isEdited || storedMonth.isCustomerEdited),
      isCustomerEdited: Boolean(
        storedMonth.isEdited || storedMonth.isCustomerEdited,
      ),
    };
  });

  const productTargets = aggregateProductsFromMonths(monthlyTargets);

  const yearlyTotal = r2(
    monthlyTargets.reduce(
      (sum, monthRow) => sum + num(monthRow.targetTotal),
      0,
    ),
  );

  return {
    ...storedTarget,
    customerId: getMainId(customer),
    customerIds: getPossibleIds(customer),
    customerName: getCustomerName(customer),
    salesPersonName:
      getAssignedSalesPersonName(customer, users) || "Unassigned",
    yearlyTotal,
    targetTotal: yearlyTotal,
    grandTotal: yearlyTotal,
    productTargets,
    products: productTargets,
    monthlyTargets,
    generated: false,
  };
};

const makeStableCustomerTargetList = (
  debtorCustomers = [],
  currentFYTarget = {},
  users = [],
) => {
  const storedRows = arr(currentFYTarget.customerTargets);

  if (storedRows.length > 0 || currentFYTarget.customerSplitLocked) {
    return debtorCustomers.map((customer) => {
      const stored = findStoredCustomerTarget(storedRows, customer);

      if (stored) return normalizeStoredCustomerTarget(customer, stored, users);

      return makeEmptyCustomerTarget(customer, users);
    });
  }

  const customerCount = debtorCustomers.length || 1;

  return debtorCustomers.map((customer) =>
    makeGeneratedCustomerTarget(
      customer,
      currentFYTarget,
      customerCount,
      users,
    ),
  );
};

const upsertYearlyTarget = (list, target) => {
  if (!target) return arr(list);

  let found = false;
  const targetId = str(target._id);
  const targetFY = str(target.fyear);

  const next = arr(list).map((item) => {
    const sameId = targetId && str(item._id) === targetId;
    const sameFY = targetFY && str(item.fyear) === targetFY;

    if (sameId || sameFY) {
      found = true;
      return { ...item, ...target };
    }

    return item;
  });

  if (!found) next.unshift(target);

  return next;
};

const recalculateYearTargetFromCustomerTargets = (
  baseTarget = {},
  customerTargets = [],
) => {
  const monthlyTargets = FY_MONTHS.map((month, index) => {
    const existingYearMonth =
      arr(baseTarget.monthlyTargets).find(
        (monthRow) => norm(monthRow.month) === norm(month),
      ) || {};

    const sourceCustomerMonths = arr(customerTargets)
      .map((customerTarget) =>
        arr(customerTarget.monthlyTargets).find(
          (monthRow) => norm(monthRow.month) === norm(month),
        ),
      )
      .filter(Boolean);

    const products = aggregateProductsFromMonths(sourceCustomerMonths);

    const targetTotal = r2(
      sourceCustomerMonths.reduce(
        (sum, monthRow) => sum + num(monthRow.targetTotal),
        0,
      ),
    );

    return {
      ...existingYearMonth,
      month,
      monthIndex: index,
      targetTotal,
      totalSecondaryQty: r2(
        products.reduce(
          (sum, p) => sum + num(first(p.secondaryQty, p.sQty)),
          0,
        ),
      ),
      totalPrimaryQty: r2(
        products.reduce(
          (sum, p) => sum + num(first(p.primaryQty, p.pQty, p.qty)),
          0,
        ),
      ),
      productTargets: products,
      products,
      isEdited: sourceCustomerMonths.some(
        (monthRow) => monthRow.isEdited || monthRow.isCustomerEdited,
      ),
      isCustomerEdited: sourceCustomerMonths.some(
        (monthRow) => monthRow.isEdited || monthRow.isCustomerEdited,
      ),
    };
  });

  const productTargets = aggregateProductsFromMonths(monthlyTargets);

  const grandTotal = r2(
    monthlyTargets.reduce(
      (sum, monthRow) => sum + num(monthRow.targetTotal),
      0,
    ),
  );

  const totalSecondaryQty = r2(
    productTargets.reduce(
      (sum, product) => sum + num(first(product.secondaryQty, product.sQty)),
      0,
    ),
  );

  const totalPrimaryQty = r2(
    productTargets.reduce(
      (sum, product) =>
        sum + num(first(product.primaryQty, product.pQty, product.qty)),
      0,
    ),
  );

  return {
    ...baseTarget,
    grandTotal,
    targetTotal: grandTotal,
    totalSecondaryQty,
    totalPrimaryQty,
    productTargets,
    products: productTargets,
    monthlyTargets,
    customerTargets,
    updatedAt: new Date().toISOString(),
  };
};

const makeRollupFromSources = (sources = []) => {
  const monthlyTargets = FY_MONTHS.map((month, index) => {
    const sourceMonths = arr(sources)
      .map((source) =>
        arr(source.monthlyTargets).find(
          (monthRow) => norm(monthRow.month) === norm(month),
        ),
      )
      .filter(Boolean);

    const products = aggregateProductsFromMonths(sourceMonths);

    const targetTotal = r2(
      sourceMonths.reduce(
        (sum, monthRow) => sum + num(monthRow.targetTotal),
        0,
      ),
    );

    return {
      month,
      monthIndex: index,
      targetTotal,
      totalSecondaryQty: r2(
        products.reduce(
          (sum, p) => sum + num(first(p.secondaryQty, p.sQty)),
          0,
        ),
      ),
      totalPrimaryQty: r2(
        products.reduce(
          (sum, p) => sum + num(first(p.primaryQty, p.pQty, p.qty)),
          0,
        ),
      ),
      productTargets: products,
      products,
      sourceCount: sources.length,
      isEdited: sourceMonths.some(
        (monthRow) => monthRow.isEdited || monthRow.isCustomerEdited,
      ),
      isCustomerEdited: sourceMonths.some(
        (monthRow) => monthRow.isEdited || monthRow.isCustomerEdited,
      ),
    };
  });

  const productTargets = aggregateProductsFromMonths(monthlyTargets);

  const yearlyTotal = r2(
    monthlyTargets.reduce(
      (sum, monthRow) => sum + num(monthRow.targetTotal),
      0,
    ),
  );

  return {
    yearlyTotal,
    targetTotal: yearlyTotal,
    grandTotal: yearlyTotal,
    productTargets,
    products: productTargets,
    monthlyTargets,
  };
};

const makeHierarchyTargets = ({
  customerTargets,
  debtorCustomers,
  users,
  roles,
}) => {
  const salesPersonRole = arr(roles).find(
    (role) => getRolePosition(role) === 2,
  );

  const salesManagerRole = arr(roles).find(
    (role) => getRolePosition(role) === 1,
  );

  const salesPersons = arr(users).filter((user) =>
    isSalesPersonUser(user, salesPersonRole),
  );

  const salesManagers = arr(users).filter((user) =>
    isSalesManagerUser(user, salesManagerRole),
  );

  const customerTargetById = new Map();

  arr(customerTargets).forEach((target) => {
    const ids = [getCustomerTargetMainId(target), ...arr(target.customerIds)]
      .map(str)
      .filter(Boolean);

    ids.forEach((id) => {
      if (!customerTargetById.has(id)) customerTargetById.set(id, target);
    });
  });

  const salesPersonTargets = salesPersons.map((salesPerson) => {
    const assignedCustomers = arr(debtorCustomers).filter((customer) =>
      isCustomerAssignedToSalesPerson(customer, salesPerson),
    );

    const assignedCustomerTargets = assignedCustomers
      .map((customer) => {
        const ids = getPossibleIds(customer);
        return ids.map((id) => customerTargetById.get(id)).find(Boolean);
      })
      .filter(Boolean);

    const rollup = makeRollupFromSources(assignedCustomerTargets);

    return {
      salesPersonId: getMainId(salesPerson),
      salesPersonIds: getPossibleIds(salesPerson),
      salesPersonName: getDisplayName(salesPerson, "Sales Person"),
      customerCount: assignedCustomerTargets.length,
      assignedCustomers: assignedCustomers.map((customer) => ({
        customerId: getMainId(customer),
        customerIds: getPossibleIds(customer),
        customerName: getCustomerName(customer),
      })),
      assignedCustomerTargetIds: assignedCustomerTargets.map(
        getCustomerTargetMainId,
      ),
      ...rollup,
      updatedAt: new Date().toISOString(),
    };
  });

  const salesPersonTargetById = new Map();

  salesPersonTargets.forEach((target) => {
    const ids = [target.salesPersonId, ...arr(target.salesPersonIds)]
      .map(str)
      .filter(Boolean);

    ids.forEach((id) => {
      if (!salesPersonTargetById.has(id)) salesPersonTargetById.set(id, target);
    });
  });

  const salesManagerTargets = salesManagers.map((manager) => {
    let assignedSalesPersons = salesPersons.filter((salesPerson) =>
      isSalesPersonAssignedToManager(salesPerson, manager),
    );

    if (salesManagers.length === 1 && assignedSalesPersons.length === 0) {
      assignedSalesPersons = salesPersons;
    }

    const assignedSalesPersonTargets = assignedSalesPersons
      .map((salesPerson) => {
        const ids = getPossibleIds(salesPerson);
        return ids.map((id) => salesPersonTargetById.get(id)).find(Boolean);
      })
      .filter(Boolean);

    const rollup = makeRollupFromSources(assignedSalesPersonTargets);

    return {
      salesManagerId: getMainId(manager),
      salesManagerIds: getPossibleIds(manager),
      salesManagerName: getDisplayName(manager, "Sales Manager"),
      salesPersonCount: assignedSalesPersonTargets.length,
      assignedSalesPersons: assignedSalesPersons.map((salesPerson) => ({
        salesPersonId: getMainId(salesPerson),
        salesPersonIds: getPossibleIds(salesPerson),
        salesPersonName: getDisplayName(salesPerson, "Sales Person"),
      })),
      assignedSalesPersonTargetIds: assignedSalesPersonTargets.map(
        (target) => target.salesPersonId,
      ),
      ...rollup,
      updatedAt: new Date().toISOString(),
    };
  });

  return {
    salesPersonTargets,
    salesManagerTargets,
  };
};

const injectOnce = () => {
  const old = document.getElementById("customer-target-page-css");
  if (old) old.remove();

  const style = document.createElement("style");
  style.id = "customer-target-page-css";

  style.textContent = `
    .ct-page{height:100vh;width:100%;overflow:hidden;background:#f8fafc;color:#0f172a;display:flex;flex-direction:column;font-family:Inter,system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif}
    .ct-top{min-height:42px;padding:5px 7px;display:flex;align-items:center;justify-content:space-between;gap:7px;background:linear-gradient(135deg,#0f172a,#1d4ed8);color:#fff;flex:0 0 auto}
    .ct-title{display:flex;flex-direction:column;gap:1px;min-width:170px}
    .ct-title h2{margin:0;font-size:15px;line-height:1;font-weight:950;white-space:nowrap}
    .ct-title span{font-size:9px;color:rgba(255,255,255,.72);font-weight:800;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;max-width:820px}
    .ct-actions{display:flex;align-items:center;gap:5px;flex-wrap:wrap;justify-content:flex-end}
    .ct-btn{height:30px;border:none;border-radius:8px;padding:0 9px;font-size:11px;font-weight:950;cursor:pointer;white-space:nowrap;transition:.12s ease}
    .ct-btn:hover{transform:translateY(-1px)}
    .ct-btn.primary{background:#16a34a;color:#fff}
    .ct-btn.light{background:#fff;color:#0f172a}
    .ct-btn.soft{background:#eff6ff;color:#1d4ed8;border:1px solid #bfdbfe}
    .ct-btn.danger{background:#fee2e2;color:#b91c1c}
    .ct-btn:disabled{opacity:.55;cursor:not-allowed;transform:none}
    .ct-filter{display:grid;grid-template-columns:120px minmax(220px,1fr) 170px 170px 170px 170px;gap:6px;align-items:end;padding:6px 7px;background:#fff;border-bottom:1px solid #dbe3ef;flex:0 0 auto}
    .ct-field{display:flex;flex-direction:column;gap:2px;min-width:0}
    .ct-label{font-size:9px;font-weight:950;color:#64748b;text-transform:uppercase;letter-spacing:.04em;line-height:1}
    .ct-input,.ct-select,.ct-stat{height:29px;border:1px solid #cbd5e1;border-radius:8px;padding:0 8px;background:#fff;color:#0f172a;outline:none;font-size:12px;font-weight:850;min-width:0}
    .ct-input:focus,.ct-select:focus{border-color:#2563eb;box-shadow:0 0 0 2px rgba(37,99,235,.12)}
    .ct-stat{display:flex;align-items:center;justify-content:space-between;background:#ecfdf5;border-color:#bbf7d0;color:#047857;font-weight:950;font-variant-numeric:tabular-nums;white-space:nowrap}
    .ct-main{flex:1 1 auto;min-height:0;display:grid;grid-template-columns:330px minmax(0,1fr);gap:8px;overflow:hidden;background:#f8fafc;padding:8px}
    .ct-left,.ct-right{min-height:0;overflow:auto;background:#fff;border:1px solid #e2e8f0;border-radius:13px;padding:8px}
    .ct-section-head{display:flex;align-items:flex-start;justify-content:space-between;gap:8px;margin-bottom:8px}
    .ct-view-title{margin:0;font-size:15px;font-weight:950;color:#0f172a}
    .ct-view-sub{margin:2px 0 0;font-size:11px;color:#64748b;font-weight:850}
    .ct-list{display:flex;flex-direction:column;gap:6px;margin-top:8px}
    .ct-row{width:100%;border:1px solid #e2e8f0;background:#f8fafc;border-radius:11px;padding:8px;display:flex;align-items:flex-start;justify-content:space-between;gap:8px;cursor:pointer;text-align:left;transition:.12s ease}
    .ct-row:hover{transform:translateY(-1px);border-color:#bfdbfe;box-shadow:0 8px 18px rgba(15,23,42,.06)}
    .ct-row.active{background:#eff6ff;border-color:#2563eb;box-shadow:0 8px 18px rgba(37,99,235,.10)}
    .ct-row b{display:block;font-size:12px;font-weight:950;color:#0f172a;line-height:1.15}
    .ct-row small{display:block;margin-top:3px;font-size:10px;font-weight:850;color:#64748b;line-height:1.2}
    .ct-row strong{font-size:11px;font-weight:950;color:#047857;white-space:nowrap;padding-top:2px}
    .ct-summary-grid{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:7px;margin-bottom:8px}
    .ct-summary-box{background:#f8fafc;border:1px solid #e2e8f0;border-radius:10px;padding:8px;min-height:54px}
    .ct-summary-box label{display:block;font-size:9px;font-weight:950;color:#64748b;text-transform:uppercase;line-height:1;margin-bottom:5px}
    .ct-summary-box b{display:block;font-size:14px;font-weight:950;color:#0f172a;line-height:1.05;font-variant-numeric:tabular-nums;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
    .ct-note{border:1px solid #fed7aa;background:#fff7ed;color:#9a3412;border-radius:10px;padding:7px 9px;font-size:11px;font-weight:900;margin-bottom:8px}
    .ct-note.blue{border-color:#bfdbfe;background:#eff6ff;color:#1e40af}
    .ct-month-grid{display:grid;grid-template-columns:repeat(6,minmax(0,1fr));gap:6px;margin-bottom:8px}
    .ct-month-card{border:1px solid #e2e8f0;background:linear-gradient(180deg,#fff,#f8fafc);border-radius:11px;padding:7px;min-height:82px;cursor:pointer;transition:.15s ease;display:flex;flex-direction:column;justify-content:space-between;gap:4px;text-align:left}
    .ct-month-card:hover{transform:translateY(-1px);border-color:#93c5fd;box-shadow:0 8px 18px rgba(15,23,42,.07)}
    .ct-month-card.selected{border-color:#2563eb;box-shadow:0 0 0 2px rgba(37,99,235,.12)}
    .ct-month-card.edited{border-color:#bbf7d0;background:linear-gradient(180deg,#ecfdf5,#fff)}
    .ct-month-card.completed{background:#e5e7eb;border-color:#cbd5e1;color:#64748b;cursor:pointer;opacity:.86;box-shadow:none;transform:none}
    .ct-month-name{font-size:13px;font-weight:950;color:#0f172a;line-height:1}
    .ct-month-card.completed .ct-month-name,.ct-month-card.completed .ct-month-total{color:#64748b}
    .ct-month-total{font-size:14px;font-weight:950;color:#047857;font-variant-numeric:tabular-nums}
    .ct-month-meta{display:flex;align-items:center;justify-content:space-between;gap:5px;font-size:9px;font-weight:900;color:#64748b}
    .ct-chip{display:inline-flex;align-items:center;height:18px;padding:0 6px;border-radius:999px;font-size:8px;font-weight:950;white-space:nowrap;background:#eff6ff;border:1px solid #bfdbfe;color:#1d4ed8}
    .ct-chip.green{background:#ecfdf5;border-color:#bbf7d0;color:#047857}
    .ct-chip.gray{background:#f1f5f9;border-color:#cbd5e1;color:#64748b}
    .ct-month-view{border:1px solid #e2e8f0;border-radius:13px;background:#fff;padding:8px;margin-top:8px}
    .ct-table-wrap{width:100%;overflow:auto;border:1px solid #e2e8f0;border-radius:12px}
    .ct-view-table{width:100%;min-width:760px;border-collapse:collapse;background:#fff}
    .ct-view-table th{background:#e2e8f0;border-bottom:1px solid #cbd5e1;padding:7px;font-size:10px;text-align:left;text-transform:uppercase;font-weight:950;color:#334155}
    .ct-view-table td{border-bottom:1px solid #eef2f7;padding:7px;font-size:12px;font-weight:850;color:#0f172a}
    .ct-view-table tr.total-row td{background:#f8fafc;font-weight:950;color:#047857}
    .ct-empty{border:1px dashed #cbd5e1;background:#fff;color:#64748b;border-radius:12px;padding:18px;font-size:13px;font-weight:900;text-align:center}
    .ct-modal-backdrop{position:fixed;inset:0;background:rgba(15,23,42,.48);display:flex;align-items:center;justify-content:center;padding:0;z-index:9999}
    .ct-modal{width:100vw;height:100vh;max-height:100vh;overflow:hidden;background:#fff;border-radius:0;border:none;box-shadow:none;display:flex;flex-direction:column}
    .ct-modal-head{padding:8px 10px;border-bottom:1px solid #e2e8f0;display:flex;align-items:flex-start;justify-content:space-between;gap:8px;flex:0 0 auto;background:#fff}
    .ct-modal-title{margin:0;font-size:16px;font-weight:950;color:#0f172a}
    .ct-modal-sub{margin:3px 0 0 0;font-size:11px;color:#64748b;font-weight:800}
    .ct-close{height:30px;width:30px;border:none;border-radius:8px;background:#f1f5f9;color:#0f172a;font-size:18px;font-weight:950;cursor:pointer}
    .ct-edit-filter{display:grid;grid-template-columns:150px 150px 130px minmax(180px,1fr) 150px;gap:5px;align-items:end;padding:5px 7px;background:#fff;border-bottom:1px solid #dbe3ef;flex:0 0 auto}
    .ct-edit-bar{min-height:34px;display:flex;align-items:center;justify-content:space-between;gap:8px;padding:4px 7px;background:#f1f5f9;border-bottom:1px solid #dbe3ef;flex:0 0 auto}
    .ct-modal-body{flex:1 1 auto;min-height:0;overflow:auto;padding:0;background:#fff}
    .ct-modal-foot{padding:8px 10px;border-top:1px solid #e2e8f0;display:flex;align-items:center;justify-content:space-between;gap:8px;flex:0 0 auto;background:#f8fafc}
    .ct-edit-table{width:100%;min-width:980px;border-collapse:separate;border-spacing:0;background:#fff;table-layout:fixed}
    .ct-edit-table thead th{position:sticky;top:0;z-index:5;background:#e2e8f0;border-bottom:1px solid #cbd5e1;border-right:1px solid #cbd5e1;padding:6px;font-size:9px;text-transform:uppercase;letter-spacing:.04em;font-weight:950;color:#334155;text-align:left;white-space:nowrap}
    .ct-edit-table tbody td{border-bottom:1px solid #e2e8f0;border-right:1px solid #eef2f7;padding:5px 6px;font-size:12px;color:#0f172a;vertical-align:middle;height:34px}
    .ct-col-product{width:32%}.ct-col-color{width:11%}.ct-col-price{width:10%}.ct-col-sec{width:15%}.ct-col-primary{width:12%}.ct-col-amount{width:14%}
    .ct-product-name{max-width:100%;font-size:12px;font-weight:950;color:#0f172a;line-height:1.15;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
    .ct-money{font-weight:950;color:#0f172a;font-variant-numeric:tabular-nums;white-space:nowrap}
    .ct-muted{font-size:9px;color:#64748b;font-weight:800;margin-top:1px;line-height:1.1;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
    .ct-cell-input{width:98px;height:28px;border:1px solid #cbd5e1;border-radius:8px;padding:0 8px;outline:none;background:#fff;color:#0f172a;font-size:12px;font-weight:900;font-variant-numeric:tabular-nums}
    .ct-cell-input:focus{border-color:#16a34a;box-shadow:0 0 0 2px rgba(22,163,74,.12)}
    .ct-total-cell{font-size:12px;font-weight:950;color:#047857;font-variant-numeric:tabular-nums;white-space:nowrap}
    @media(max-width:1250px){.ct-filter{grid-template-columns:repeat(3,minmax(0,1fr))}.ct-main{grid-template-columns:1fr;overflow:auto}.ct-summary-grid{grid-template-columns:repeat(2,minmax(0,1fr))}.ct-month-grid{grid-template-columns:repeat(4,minmax(0,1fr))}.ct-edit-filter{grid-template-columns:repeat(3,minmax(0,1fr))}}
    @media(max-width:760px){.ct-page{overflow:auto;height:auto;min-height:100vh}.ct-filter{grid-template-columns:1fr}.ct-main{display:block}.ct-left,.ct-right{margin-bottom:8px}.ct-summary-grid,.ct-month-grid{grid-template-columns:1fr}.ct-title span{display:none}.ct-edit-filter{grid-template-columns:1fr}.ct-modal-foot{align-items:flex-start;flex-direction:column}}
  `;

  document.head.appendChild(style);
};

export default function CustomerTarget() {
  const currentUser = getUser();
  const database = currentUser.database || currentUser.db || "";

  const [fy, setFy] = useState(getCurrentFY());
  const [productList, setProductList] = useState([]);
  const [savedTargets, setSavedTargets] = useState([]);
  const [roles, setRoles] = useState([]);
  const [users, setUsers] = useState([]);
  const [customers, setCustomers] = useState([]);

  const [loadingProducts, setLoadingProducts] = useState(false);
  const [loadingTargets, setLoadingTargets] = useState(false);
  const [loadingPeople, setLoadingPeople] = useState(false);
  const [savingSplit, setSavingSplit] = useState(false);
  const [savingMonth, setSavingMonth] = useState(false);
  const [autoSplitting, setAutoSplitting] = useState(false);

  const [customerSearch, setCustomerSearch] = useState("");
  const [selectedCustomerId, setSelectedCustomerId] = useState("");
  const [monthView, setMonthView] = useState(null);

  const [monthEditor, setMonthEditor] = useState(null);
  const [monthQtyMap, setMonthQtyMap] = useState({});
  const [monthSearch, setMonthSearch] = useState("");
  const [monthCategoryFilter, setMonthCategoryFilter] = useState(FILTER_ALL);
  const [monthSubCategoryFilter, setMonthSubCategoryFilter] =
    useState(FILTER_ALL);
  const [monthColorFilter, setMonthColorFilter] = useState(FILTER_ALL);

  useEffect(() => {
    injectOnce();
  }, []);

  useEffect(() => {
    loadProducts();
    loadSavedTargets();
    loadPeople();
  }, []);

  const currentFYTarget = useMemo(() => {
    return (
      savedTargets.find(
        (target) =>
          String(target.fyear) === String(fy) &&
          norm(target.status || "Active") !== "deleted",
      ) || null
    );
  }, [savedTargets, fy]);

  const debtorCustomers = useMemo(() => {
    return arr(customers)
      .filter(isDebtorCustomer)
      .sort((a, b) =>
        String(getCustomerName(a)).localeCompare(String(getCustomerName(b))),
      );
  }, [customers]);

  const customerTargetList = useMemo(() => {
    if (!currentFYTarget) return [];

    return makeStableCustomerTargetList(
      debtorCustomers,
      currentFYTarget,
      users,
    ).sort((a, b) =>
      String(a.customerName || "").localeCompare(String(b.customerName || "")),
    );
  }, [currentFYTarget, debtorCustomers, users]);

  const filteredCustomerTargetList = useMemo(() => {
    const q = norm(customerSearch);
    if (!q) return customerTargetList;

    return customerTargetList.filter((target) =>
      [
        target.customerName,
        target.salesPersonName,
        target.customerId,
        arr(target.customerIds).join(" "),
      ]
        .map(str)
        .join(" ")
        .toLowerCase()
        .includes(q),
    );
  }, [customerTargetList, customerSearch]);

  const selectedCustomerTarget = useMemo(() => {
    if (!customerTargetList.length) return null;

    return (
      customerTargetList.find(
        (target) => getCustomerTargetMainId(target) === selectedCustomerId,
      ) || customerTargetList[0]
    );
  }, [customerTargetList, selectedCustomerId]);

  useEffect(() => {
    if (!selectedCustomerTarget) {
      setSelectedCustomerId("");
      setMonthView(null);
      return;
    }

    const id = getCustomerTargetMainId(selectedCustomerTarget);

    if (!selectedCustomerId || selectedCustomerId !== id) {
      setSelectedCustomerId(id);
    }
  }, [selectedCustomerTarget, selectedCustomerId]);

  const selectedMonthProducts = useMemo(
    () => getMonthProducts(monthView?.monthRow),
    [monthView],
  );

  const selectedMonthLocked = monthView?.monthRow
    ? isMonthCompleted(fy, monthView.monthRow.month)
    : false;

  const monthProductSource = useMemo(() => {
    if (!monthEditor) return [];

    const map = new Map();

    productList.forEach((product) => {
      const id = getProductId(product);
      if (id) map.set(id, product);
    });

    getMonthProducts(monthEditor.monthRow).forEach((product) => {
      const id = str(product?.productId || product?._id || product?.id);
      if (!id) return;

      if (!map.has(id)) {
        map.set(id, savedProductToProductMasterShape(product));
      }
    });

    return Array.from(map.values());
  }, [monthEditor, productList]);

  const allMonthRows = useMemo(() => {
    return monthProductSource.map((product) => {
      const id =
        getProductId(product) || str(product?.productId || product?._id);

      return productToRow(
        {
          ...product,
          _id: id,
          id,
          productId: id,
        },
        monthQtyMap,
      );
    });
  }, [monthProductSource, monthQtyMap]);

  const monthCategoryOptions = useMemo(
    () => makeOptionList(monthProductSource, getProductCategory),
    [monthProductSource],
  );

  const monthSubCategoryOptions = useMemo(() => {
    const sourceRows =
      monthCategoryFilter === FILTER_ALL
        ? monthProductSource
        : monthProductSource.filter(
            (product) =>
              norm(getProductCategory(product)) === norm(monthCategoryFilter),
          );

    return makeOptionList(sourceRows, getProductSubCategory);
  }, [monthProductSource, monthCategoryFilter]);

  const monthColorOptions = useMemo(() => {
    let sourceRows = monthProductSource;

    if (monthCategoryFilter !== FILTER_ALL) {
      sourceRows = sourceRows.filter(
        (product) =>
          norm(getProductCategory(product)) === norm(monthCategoryFilter),
      );
    }

    if (monthSubCategoryFilter !== FILTER_ALL) {
      sourceRows = sourceRows.filter(
        (product) =>
          norm(getProductSubCategory(product)) === norm(monthSubCategoryFilter),
      );
    }

    return makeOptionList(sourceRows, getProductColor);
  }, [monthProductSource, monthCategoryFilter, monthSubCategoryFilter]);

  const monthRows = useMemo(() => {
    const q = norm(monthSearch);

    return allMonthRows.filter((row) => {
      const categoryOk =
        monthCategoryFilter === FILTER_ALL ||
        norm(row.category) === norm(monthCategoryFilter);

      const subOk =
        monthSubCategoryFilter === FILTER_ALL ||
        norm(row.subCategory) === norm(monthSubCategoryFilter);

      const colorOk =
        monthColorFilter === FILTER_ALL ||
        norm(row.color) === norm(monthColorFilter);

      const searchText = [
        row.productName,
        row.category,
        row.subCategory,
        row.color,
        row.product?.sId,
        row.product?.barcode,
        row.product?.Barcode,
        row.product?.HSN_Code,
        row.product?.hsnCode,
      ]
        .map(str)
        .join(" ")
        .toLowerCase();

      return categoryOk && subOk && colorOk && (!q || searchText.includes(q));
    });
  }, [
    allMonthRows,
    monthSearch,
    monthCategoryFilter,
    monthSubCategoryFilter,
    monthColorFilter,
  ]);

  const monthTotal = useMemo(
    () => allMonthRows.reduce((sum, row) => sum + num(row.total), 0),
    [allMonthRows],
  );

  const monthVisibleTotal = useMemo(
    () => monthRows.reduce((sum, row) => sum + num(row.total), 0),
    [monthRows],
  );

  const currentStatusText = useMemo(() => {
    if (!currentFYTarget) return "No yearly target found for selected FY.";

    if (
      currentFYTarget.customerSplitLocked ||
      arr(currentFYTarget.customerTargets).length > 0
    ) {
      return "Customer-wise targets are locked. Editing one customer will not change other customers.";
    }

    if (autoSplitting) {
      return "Saving customer-wise targets automatically...";
    }

    return "Customer split is not locked yet. It will be saved automatically.";
  }, [currentFYTarget, autoSplitting]);

  useEffect(() => {
    if (!database || !monthEditor?.monthRow?.month) return;

    const id = getCustomerTargetMainId(monthEditor.customerTarget);
    const month = monthEditor.monthRow.month;

    const t = setTimeout(() => {
      safeWriteLS(monthDraftKey(database, fy, id, month), {
        qtyMap: monthQtyMap,
        search: monthSearch,
        categoryFilter: monthCategoryFilter,
        subCategoryFilter: monthSubCategoryFilter,
        colorFilter: monthColorFilter,
        updatedAt: new Date().toISOString(),
      });
    }, 120);

    return () => clearTimeout(t);
  }, [
    database,
    fy,
    monthEditor,
    monthQtyMap,
    monthSearch,
    monthCategoryFilter,
    monthSubCategoryFilter,
    monthColorFilter,
  ]);

  const loadProducts = async () => {
    const db = getUser().database || getUser().db || "";

    if (!db) {
      setProductList([]);
      return;
    }

    const cached = safeReadLS(productsCacheKey(db), null);
    if (cached?.items?.length) setProductList(cached.items);

    try {
      setLoadingProducts(true);

      const res = await axios.get(`${PurchaseProductList_Product}${db}`);
      const payload = res?.data ?? res;

      const list = arr(
        payload.Product || payload.product || payload.data,
      ).filter(
        (product) => !norm(product.status) || norm(product.status) === "active",
      );

      setProductList(list);

      safeWriteLS(productsCacheKey(db), {
        updatedAt: new Date().toISOString(),
        items: list,
      });
    } catch (err) {
      console.log("[CustomerTarget] loadProducts error:", err);
      if (!cached?.items?.length) setProductList([]);
    } finally {
      setLoadingProducts(false);
    }
  };

  const loadSavedTargets = async () => {
    const db = getUser().database || getUser().db || "";

    if (!db) {
      setSavedTargets([]);
      return;
    }

    const cached = safeReadLS(targetsCacheKey(db), null);
    if (cached?.items?.length) setSavedTargets(cached.items);

    try {
      setLoadingTargets(true);

      const res = await axios.get(`${API.list}/${db}`);
      const payload = res?.data ?? res;
      const list = arr(payload.data || payload.YearlyTarget || []);

      setSavedTargets(list);

      safeWriteLS(targetsCacheKey(db), {
        updatedAt: new Date().toISOString(),
        items: list,
      });
    } catch (err) {
      console.log("[CustomerTarget] loadSavedTargets error:", err);
      if (!cached?.items?.length) setSavedTargets([]);
    } finally {
      setLoadingTargets(false);
    }
  };

  const loadPeople = async () => {
    const userinfo = getUser();
    const uid = userinfo?._id || userinfo?.id || "";
    const db = userinfo?.database || userinfo?.db || "";

    if (!uid || !db) {
      setRoles([]);
      setUsers([]);
      setCustomers([]);
      return;
    }

    const cached = safeReadLS(peopleCacheKey(db), null);

    if (cached?.users || cached?.customers || cached?.roles) {
      setRoles(arr(cached.roles));
      setUsers(arr(cached.users));
      setCustomers(arr(cached.customers));
    }

    try {
      setLoadingPeople(true);

      const [roleRes, userRes, customerRes] = await Promise.allSettled([
        axios.get(`/assign-role/view-assign-role/${db}`),
        axios.get(buildUserApiPath(uid, db)),
        CreateCustomerList(uid, db),
      ]);

      let nextRoles = [];

      if (roleRes.status === "fulfilled") {
        const departments = arr(roleRes?.value?.data?.Department);
        const salesDept = departments.find(
          (department) =>
            norm(department?.departmentName?.departmentName) === "sales",
        );

        nextRoles = arr(salesDept?.roles)
          .map((role) => ({
            ...role,
            rolePosition: getRolePosition(role),
            roleName: getRoleName(role),
          }))
          .filter((role) => getRoleName(role))
          .sort((a, b) => getRolePosition(a) - getRolePosition(b));
      }

      let nextUsers = [];

      if (userRes.status === "fulfilled") {
        nextUsers = extractUserListFromResponse(userRes?.value?.data).filter(
          (user) => {
            const status = norm(user?.status);
            const roleName = norm(user?.rolename?.roleName);

            if (status && status !== "active") return false;
            if (roleName === "customer") return false;

            return true;
          },
        );
      }

      let nextCustomers = [];

      if (customerRes.status === "fulfilled") {
        nextCustomers = arr(customerRes?.value?.Customer);
      }

      setRoles(nextRoles);
      setUsers(nextUsers);
      setCustomers(nextCustomers);

      safeWriteLS(peopleCacheKey(db), {
        updatedAt: new Date().toISOString(),
        roles: nextRoles,
        users: nextUsers,
        customers: nextCustomers,
      });
    } catch (err) {
      console.log("[CustomerTarget] loadPeople error:", err);

      if (!cached?.roles) setRoles([]);
      if (!cached?.users) setUsers([]);
      if (!cached?.customers) setCustomers([]);
    } finally {
      setLoadingPeople(false);
    }
  };

  const refreshAll = () => {
    loadProducts();
    loadSavedTargets();
    loadPeople();
  };

  const resetMonthFilters = () => {
    setMonthSearch("");
    setMonthCategoryFilter(FILTER_ALL);
    setMonthSubCategoryFilter(FILTER_ALL);
    setMonthColorFilter(FILTER_ALL);
  };

  const selectCustomerTarget = (target) => {
    setSelectedCustomerId(getCustomerTargetMainId(target));
    setMonthView(null);
    setMonthEditor(null);
    setMonthQtyMap({});
    resetMonthFilters();
  };

  const openMonthView = (customerTarget, monthRow) => {
    setMonthView({ customerTarget, monthRow });
  };

  const openMonthEditor = (customerTarget, monthRow) => {
    if (isMonthCompleted(fy, monthRow.month)) {
      Swal.fire({
        icon: "info",
        title: "Month Locked",
        text: `${monthRow.month} is completed, so this customer month target cannot be edited.`,
        confirmButtonColor: "#64748b",
      });
      return;
    }

    const id = getCustomerTargetMainId(customerTarget);
    const draft = safeReadLS(
      monthDraftKey(database, fy, id, monthRow.month),
      null,
    );

    const map = {};

    getMonthProducts(monthRow).forEach((product) => {
      const productId = str(product.productId || product._id || product.id);
      if (productId) map[productId] = num(product.secondaryQty || product.sQty);
    });

    setMonthQtyMap(draft?.qtyMap || map);
    setMonthSearch(draft?.search || "");
    setMonthCategoryFilter(draft?.categoryFilter || FILTER_ALL);
    setMonthSubCategoryFilter(draft?.subCategoryFilter || FILTER_ALL);
    setMonthColorFilter(draft?.colorFilter || FILTER_ALL);
    setMonthEditor({ customerTarget, monthRow });
  };

  const setMonthSecondaryQty = (productId, value) => {
    setMonthQtyMap((prev) => ({
      ...prev,
      [productId]: Math.max(num(value), 0),
    }));
  };

  const saveGeneratedSplit = async (options = {}) => {
    const silent = Boolean(options.silent);

    try {
      if (!currentFYTarget?._id) {
        if (!silent) {
          Swal.fire({
            icon: "warning",
            title: "No Yearly Target",
            text: `Please create yearly target for ${fy} first.`,
            confirmButtonColor: "#f59e0b",
          });
        }

        return false;
      }

      if (!debtorCustomers.length) {
        if (!silent) {
          Swal.fire({
            icon: "warning",
            title: "No Debtor Customers",
            text: "No debtor customer found for dividing target.",
            confirmButtonColor: "#f59e0b",
          });
        }

        return false;
      }

      if (
        arr(currentFYTarget.customerTargets).length > 0 ||
        currentFYTarget.customerSplitLocked
      ) {
        if (!silent) {
          Swal.fire({
            icon: "info",
            title: "Customer Split Already Locked",
            text: "Customer split is already saved. Edit only individual customer target now.",
            confirmButtonColor: "#2563eb",
          });
        }

        return true;
      }

      setSavingSplit(true);
      if (silent) setAutoSplitting(true);

      const equalCustomerTargets = debtorCustomers.map((customer) =>
        makeGeneratedCustomerTarget(
          customer,
          currentFYTarget,
          debtorCustomers.length,
          users,
        ),
      );

      const hierarchyTargets = makeHierarchyTargets({
        customerTargets: equalCustomerTargets,
        debtorCustomers,
        users,
        roles,
      });

      const updatedYearTarget = recalculateYearTargetFromCustomerTargets(
        currentFYTarget,
        equalCustomerTargets,
      );

      const finalYearTarget = {
        ...updatedYearTarget,
        database,
        fyear: fy,
        customerSplitLocked: true,
        splitBaseGrandTotal: Number(currentFYTarget.grandTotal || 0),
        splitBaseMonthlyTargets: getMonthlyTargets(currentFYTarget),
        splitBaseProductTargets: getSavedProducts(currentFYTarget),
        customerTargets: equalCustomerTargets,
        salesPersonTargets: hierarchyTargets.salesPersonTargets,
        salesManagerTargets: hierarchyTargets.salesManagerTargets,
        updated_by: currentUser._id || currentUser.id || "",
        updatedAt: new Date().toISOString(),
      };

      const optimisticList = upsertYearlyTarget(savedTargets, finalYearTarget);

      setSavedTargets(optimisticList);

      safeWriteLS(targetsCacheKey(database), {
        updatedAt: new Date().toISOString(),
        items: optimisticList,
      });

      const { _id, __v, createdAt, updatedAt, ...payloadWithoutIds } =
        finalYearTarget;

      await axios.put(`${API.update}/${currentFYTarget._id}`, {
        ...payloadWithoutIds,
        database,
        fyear: fy,
        customerSplitLocked: true,
        splitBaseGrandTotal: Number(currentFYTarget.grandTotal || 0),
        splitBaseMonthlyTargets: getMonthlyTargets(currentFYTarget),
        splitBaseProductTargets: getSavedProducts(currentFYTarget),
        grandTotal: Number(finalYearTarget.grandTotal || 0),
        targetTotal: Number(finalYearTarget.grandTotal || 0),
        totalSecondaryQty: Number(finalYearTarget.totalSecondaryQty || 0),
        totalPrimaryQty: Number(finalYearTarget.totalPrimaryQty || 0),
        productTargets: finalYearTarget.productTargets,
        products: finalYearTarget.products,
        monthlyTargets: finalYearTarget.monthlyTargets,
        customerTargets: equalCustomerTargets,
        salesPersonTargets: hierarchyTargets.salesPersonTargets,
        salesManagerTargets: hierarchyTargets.salesManagerTargets,
        updated_by: currentUser._id || currentUser.id || "",
      });

      if (!silent) {
        Swal.fire({
          icon: "success",
          title: "Customer Split Locked",
          text: "Equal split is saved customer-wise. Future edits affect only selected customer and hierarchy roll-up.",
          confirmButtonColor: "#16a34a",
        });
      }

      loadSavedTargets();
      return true;
    } catch (err) {
      console.log("[CustomerTarget] saveGeneratedSplit error:", err);

      if (!silent) {
        Swal.fire({
          icon: "error",
          title: "Save Failed",
          text:
            err?.response?.data?.message ||
            err?.response?.data?.error ||
            "Unable to save customer split.",
          confirmButtonColor: "#dc2626",
        });
      }

      loadSavedTargets();
      return false;
    } finally {
      setSavingSplit(false);
      if (silent) setAutoSplitting(false);
    }
  };

  useEffect(() => {
    if (!database || !currentFYTarget?._id) return;
    if (loadingTargets || loadingPeople || savingSplit || autoSplitting) return;
    if (!debtorCustomers.length) return;

    if (
      currentFYTarget.customerSplitLocked ||
      arr(currentFYTarget.customerTargets).length > 0
    ) {
      return;
    }

    const key = autoSplitDoneKey(database, fy, currentFYTarget._id);

    if (safeReadLS(key, false)) return;

    safeWriteLS(key, true);

    saveGeneratedSplit({ silent: true }).then((saved) => {
      if (!saved) safeRemoveLS(key);
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    database,
    fy,
    currentFYTarget?._id,
    currentFYTarget?.customerSplitLocked,
    arr(currentFYTarget?.customerTargets).length,
    debtorCustomers.length,
    loadingTargets,
    loadingPeople,
    savingSplit,
    autoSplitting,
  ]);

  const saveCustomerMonthTarget = async () => {
    try {
      if (!currentFYTarget?._id || !monthEditor?.customerTarget) return;

      const monthName = monthEditor.monthRow.month;

      if (isMonthCompleted(fy, monthName)) {
        Swal.fire({
          icon: "info",
          title: "Month Locked",
          text: `${monthName} is completed, so this customer month target cannot be edited.`,
          confirmButtonColor: "#64748b",
        });
        return;
      }

      const productTargets = makeProductPayload(
        allMonthRows.filter((row) => num(row.secondaryQty) > 0),
      );

      if (!productTargets.length) {
        Swal.fire({
          icon: "warning",
          title: "No Customer Month Target",
          text: "Please enter target quantity for at least one product.",
          confirmButtonColor: "#f59e0b",
        });
        return;
      }

      const selectedId = getCustomerTargetMainId(monthEditor.customerTarget);
      const editedMonthIndex = FY_MONTHS.indexOf(monthName);

      if (editedMonthIndex < 0) {
        Swal.fire({
          icon: "error",
          title: "Invalid Month",
          text: "Selected financial year month is invalid.",
          confirmButtonColor: "#dc2626",
        });
        return;
      }

      setSavingMonth(true);

      const editedTotal = r2(
        productTargets.reduce(
          (sum, product) =>
            sum +
            num(first(product.total, product.totalPrice, product.targetAmount)),
          0,
        ),
      );

      const editedSecondaryQty = r2(
        productTargets.reduce(
          (sum, product) =>
            sum + num(first(product.secondaryQty, product.sQty)),
          0,
        ),
      );

      const editedPrimaryQty = r2(
        productTargets.reduce(
          (sum, product) =>
            sum + num(first(product.primaryQty, product.pQty, product.qty)),
          0,
        ),
      );

      const updatedCustomerTargets = customerTargetList.map(
        (customerTarget) => {
          const customerId = getCustomerTargetMainId(customerTarget);

          if (customerId !== selectedId) return customerTarget;

          const updatedMonthlyTargets = getMonthlyTargets(customerTarget).map(
            (monthRow) => {
              const monthIndex = FY_MONTHS.indexOf(monthRow.month);

              const shouldUpdate =
                monthIndex >= editedMonthIndex &&
                !isMonthCompleted(fy, monthRow.month);

              if (!shouldUpdate) return monthRow;

              return {
                ...monthRow,
                targetTotal: editedTotal,
                totalSecondaryQty: editedSecondaryQty,
                totalPrimaryQty: editedPrimaryQty,
                productTargets,
                products: productTargets,
                isEdited: true,
                isCustomerEdited: true,
                editedAt: new Date().toISOString(),
                edited_by: currentUser._id || currentUser.id || "",
              };
            },
          );

          const yearlyProducts = aggregateProductsFromMonths(
            updatedMonthlyTargets,
          );

          const yearlyTotal = r2(
            updatedMonthlyTargets.reduce(
              (sum, monthRow) => sum + num(monthRow.targetTotal),
              0,
            ),
          );

          return {
            ...customerTarget,
            monthlyTargets: updatedMonthlyTargets,
            productTargets: yearlyProducts,
            products: yearlyProducts,
            yearlyTotal,
            targetTotal: yearlyTotal,
            grandTotal: yearlyTotal,
            isEdited: true,
            generated: false,
            updatedAt: new Date().toISOString(),
            updated_by: currentUser._id || currentUser.id || "",
          };
        },
      );

      const hierarchyTargets = makeHierarchyTargets({
        customerTargets: updatedCustomerTargets,
        debtorCustomers,
        users,
        roles,
      });

      const updatedYearTarget = recalculateYearTargetFromCustomerTargets(
        currentFYTarget,
        updatedCustomerTargets,
      );

      const finalYearTarget = {
        ...updatedYearTarget,
        database,
        fyear: fy,
        customerSplitLocked: true,
        splitBaseGrandTotal:
          Number(currentFYTarget.splitBaseGrandTotal || 0) ||
          Number(currentFYTarget.grandTotal || 0),
        splitBaseMonthlyTargets: arr(currentFYTarget.splitBaseMonthlyTargets)
          .length
          ? currentFYTarget.splitBaseMonthlyTargets
          : getMonthlyTargets(currentFYTarget),
        splitBaseProductTargets: arr(currentFYTarget.splitBaseProductTargets)
          .length
          ? currentFYTarget.splitBaseProductTargets
          : getSavedProducts(currentFYTarget),
        customerTargets: updatedCustomerTargets,
        salesPersonTargets: hierarchyTargets.salesPersonTargets,
        salesManagerTargets: hierarchyTargets.salesManagerTargets,
        updated_by: currentUser._id || currentUser.id || "",
        updatedAt: new Date().toISOString(),
      };

      const optimisticList = upsertYearlyTarget(savedTargets, finalYearTarget);

      setSavedTargets(optimisticList);

      safeWriteLS(targetsCacheKey(database), {
        updatedAt: new Date().toISOString(),
        items: optimisticList,
      });

      const { _id, __v, createdAt, updatedAt, ...payloadWithoutIds } =
        finalYearTarget;

      await axios.put(`${API.update}/${currentFYTarget._id}`, {
        ...payloadWithoutIds,
        database,
        fyear: fy,
        customerSplitLocked: true,
        grandTotal: Number(finalYearTarget.grandTotal || 0),
        targetTotal: Number(finalYearTarget.grandTotal || 0),
        totalSecondaryQty: Number(finalYearTarget.totalSecondaryQty || 0),
        totalPrimaryQty: Number(finalYearTarget.totalPrimaryQty || 0),
        productTargets: finalYearTarget.productTargets,
        products: finalYearTarget.products,
        monthlyTargets: finalYearTarget.monthlyTargets,
        customerTargets: updatedCustomerTargets,
        salesPersonTargets: hierarchyTargets.salesPersonTargets,
        salesManagerTargets: hierarchyTargets.salesManagerTargets,
        splitBaseGrandTotal: finalYearTarget.splitBaseGrandTotal,
        splitBaseMonthlyTargets: finalYearTarget.splitBaseMonthlyTargets,
        splitBaseProductTargets: finalYearTarget.splitBaseProductTargets,
        updated_by: currentUser._id || currentUser.id || "",
      });

      safeRemoveLS(
        monthDraftKey(database, fy, selectedId, monthEditor.monthRow.month),
      );

      const updatedSelectedCustomer = updatedCustomerTargets.find(
        (target) => getCustomerTargetMainId(target) === selectedId,
      );

      const updatedSelectedMonth =
        arr(updatedSelectedCustomer?.monthlyTargets).find(
          (monthRow) => norm(monthRow.month) === norm(monthName),
        ) || null;

      setMonthEditor(null);
      setMonthQtyMap({});
      resetMonthFilters();

      if (updatedSelectedCustomer && updatedSelectedMonth) {
        setMonthView({
          customerTarget: updatedSelectedCustomer,
          monthRow: updatedSelectedMonth,
        });
      }

      Swal.fire({
        icon: "success",
        title: "Customer Target Updated",
        text: `${monthName} target updated for selected customer only. Sales person and sales manager roll-up also updated.`,
        confirmButtonColor: "#16a34a",
      });

      loadSavedTargets();
    } catch (err) {
      console.log("[CustomerTarget] saveCustomerMonthTarget error:", err);

      Swal.fire({
        icon: "error",
        title: "Customer Month Update Failed",
        text:
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          "Unable to update customer target.",
        confirmButtonColor: "#dc2626",
      });

      loadSavedTargets();
    } finally {
      setSavingMonth(false);
    }
  };

  const renderProductViewTable = (products = []) => {
    const rows = arr(products);

    const total = rows.reduce(
      (sum, product) =>
        sum +
        num(first(product.total, product.totalPrice, product.targetAmount)),
      0,
    );

    if (!rows.length) {
      return <div className="ct-empty">No product target found.</div>;
    }

    return (
      <div className="ct-table-wrap">
        <table className="ct-view-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Product</th>
              <th>Category</th>
              <th>Color</th>
              <th>Secondary Qty</th>
              <th>Primary Qty</th>
              <th>Amount</th>
            </tr>
          </thead>

          <tbody>
            {rows.map((product, index) => (
              <tr key={`${getSavedProductId(product)}-${index}`}>
                <td>{index + 1}</td>

                <td>
                  {product.productName || product.Product_Title || product.name}
                </td>

                <td>
                  {product.category || product.catageory || "-"}
                  <div className="ct-view-sub">
                    {product.subCategory || product.SubCategory || "-"}
                  </div>
                </td>

                <td>{product.color || "-"}</td>

                <td>
                  {Number(
                    num(first(product.secondaryQty, product.sQty)),
                  ).toFixed(2)}
                </td>

                <td>
                  {Number(
                    num(first(product.primaryQty, product.pQty, product.qty)),
                  ).toFixed(2)}
                </td>

                <td>
                  {money(
                    first(
                      product.total,
                      product.totalPrice,
                      product.targetAmount,
                    ),
                  )}
                </td>
              </tr>
            ))}

            <tr className="total-row">
              <td colSpan={6}>Total</td>
              <td>{money(total)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  };

  const renderEditProductTable = () => {
    if (!monthRows.length) {
      return (
        <div className="ct-empty">No product found for current filter.</div>
      );
    }

    return (
      <table className="ct-edit-table">
        <thead>
          <tr>
            <th className="ct-col-product">Product</th>
            <th>Category</th>
            <th className="ct-col-color">Color</th>
            <th className="ct-col-price">Rate</th>
            <th className="ct-col-sec">Secondary Qty</th>
            <th className="ct-col-primary">Primary Qty</th>
            <th className="ct-col-amount">Amount</th>
          </tr>
        </thead>

        <tbody>
          {monthRows.map((row) => (
            <tr key={row.id}>
              <td>
                <div className="ct-product-name">{row.productName}</div>
                <div className="ct-muted">
                  {row.secondaryUnitName} × {row.secondarySize} ={" "}
                  {row.primaryUnitName}
                </div>
              </td>

              <td>
                {row.category}
                <div className="ct-muted">{row.subCategory}</div>
              </td>

              <td>{row.color}</td>

              <td className="ct-money">{money(row.saleRate)}</td>

              <td>
                <input
                  className="ct-cell-input"
                  type="number"
                  min="0"
                  step="0.01"
                  value={monthQtyMap[row.id] ?? ""}
                  onChange={(e) => setMonthSecondaryQty(row.id, e.target.value)}
                />
              </td>

              <td>
                {Number(row.primaryQty).toFixed(2)}
                <div className="ct-muted">{row.primaryUnitName}</div>
              </td>

              <td className="ct-total-cell">{money(row.total)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  const loading = loadingProducts || loadingTargets || loadingPeople;

  const totalCustomerTarget = customerTargetList.reduce(
    (sum, target) => sum + num(target.yearlyTotal || target.grandTotal),
    0,
  );

  return (
    <div className="ct-page">
      <div className="ct-top">
        <div className="ct-title">
          <h2>Customer Target</h2>
          <span>
            Create yearly target first. This page saves customer-wise target
            separately and rolls up assigned sales person / sales manager
            target.
          </span>
        </div>

        <div className="ct-actions">
          <button type="button" className="ct-btn light" onClick={refreshAll}>
            {loading ? "Loading..." : "Refresh"}
          </button>

          <button
            type="button"
            className="ct-btn primary"
            onClick={() => saveGeneratedSplit()}
            disabled={savingSplit || autoSplitting || !currentFYTarget}
          >
            {autoSplitting
              ? "Auto Saving..."
              : savingSplit
                ? "Saving..."
                : "Save Equal Split"}
          </button>
        </div>
      </div>

      <div className="ct-filter">
        <div className="ct-field">
          <label className="ct-label">Financial Year</label>

          <select
            className="ct-select"
            value={fy}
            onChange={(e) => {
              setFy(e.target.value);
              setSelectedCustomerId("");
              setMonthView(null);
              setMonthEditor(null);
            }}
          >
            {getFYOptions().map((item) => (
              <option key={item} value={item}>
                {item}
              </option>
            ))}
          </select>
        </div>

        <div className="ct-field">
          <label className="ct-label">Search Customer</label>

          <input
            className="ct-input"
            value={customerSearch}
            onChange={(e) => setCustomerSearch(e.target.value)}
            placeholder="Search customer / sales person"
          />
        </div>

        <div className="ct-field">
          <label className="ct-label">Customers</label>
          <div className="ct-stat">
            <span>{customerTargetList.length}</span>
            <b>{filteredCustomerTargetList.length}</b>
          </div>
        </div>

        <div className="ct-field">
          <label className="ct-label">Yearly Target</label>
          <div className="ct-stat">
            <span>Year</span>
            <b>{money(currentFYTarget?.grandTotal || 0)}</b>
          </div>
        </div>

        <div className="ct-field">
          <label className="ct-label">Customer Total</label>
          <div className="ct-stat">
            <span>Split</span>
            <b>{money(totalCustomerTarget)}</b>
          </div>
        </div>

        <div className="ct-field">
          <label className="ct-label">Status</label>
          <div className="ct-stat">
            <span>
              {currentFYTarget?.customerSplitLocked ||
              arr(currentFYTarget?.customerTargets).length
                ? "Locked"
                : "Open"}
            </span>
            <b>{loading ? "..." : "Ready"}</b>
          </div>
        </div>
      </div>

      {!currentFYTarget ? (
        <div className="ct-main">
          <div className="ct-left">
            <div className="ct-empty">No yearly target found.</div>
          </div>

          <div className="ct-right">
            <div className="ct-empty">
              Please create yearly target for FY {fy} from Yearly Target page
              first.
            </div>
          </div>
        </div>
      ) : (
        <div className="ct-main">
          <div className="ct-left">
            <div className="ct-section-head">
              <div>
                <h3 className="ct-view-title">Customers</h3>
                <p className="ct-view-sub">{currentStatusText}</p>
              </div>
            </div>

            {!currentFYTarget.customerSplitLocked &&
            !arr(currentFYTarget.customerTargets).length ? (
              <div className="ct-note">
                Equal split is being saved customer-wise automatically. Once
                locked, yearly target changes will not re-divide customers.
              </div>
            ) : null}

            <div className="ct-list">
              {filteredCustomerTargetList.map((target) => {
                const active =
                  getCustomerTargetMainId(target) === selectedCustomerId;

                return (
                  <button
                    type="button"
                    key={getCustomerTargetMainId(target)}
                    className={`ct-row ${active ? "active" : ""}`}
                    onClick={() => selectCustomerTarget(target)}
                  >
                    <span>
                      <b>{target.customerName}</b>
                      <small>
                        Sales Person: {target.salesPersonName || "Unassigned"}
                      </small>
                      <small>
                        Products: {arr(target.productTargets).length} • Months:{" "}
                        {arr(target.monthlyTargets).length}
                      </small>
                    </span>

                    <strong>
                      {money(target.yearlyTotal || target.grandTotal || 0)}
                    </strong>
                  </button>
                );
              })}

              {!filteredCustomerTargetList.length ? (
                <div className="ct-empty">No customer found.</div>
              ) : null}
            </div>
          </div>

          <div className="ct-right">
            {!selectedCustomerTarget ? (
              <div className="ct-empty">Select a customer to view target.</div>
            ) : (
              <>
                <div className="ct-section-head">
                  <div>
                    <h3 className="ct-view-title">
                      {selectedCustomerTarget.customerName}
                    </h3>
                    <p className="ct-view-sub">
                      Sales Person:{" "}
                      {selectedCustomerTarget.salesPersonName || "Unassigned"} •
                      FY: {fy}
                    </p>
                  </div>

                  <button
                    type="button"
                    className="ct-btn soft"
                    onClick={() => {
                      setMonthView(null);
                      setMonthEditor(null);
                    }}
                  >
                    Clear Month
                  </button>
                </div>

                <div className="ct-summary-grid">
                  <div className="ct-summary-box">
                    <label>Customer Target</label>
                    <b>
                      {money(
                        selectedCustomerTarget.yearlyTotal ||
                          selectedCustomerTarget.grandTotal ||
                          0,
                      )}
                    </b>
                  </div>

                  <div className="ct-summary-box">
                    <label>Products</label>
                    <b>{arr(selectedCustomerTarget.productTargets).length}</b>
                  </div>

                  <div className="ct-summary-box">
                    <label>Months</label>
                    <b>{arr(selectedCustomerTarget.monthlyTargets).length}</b>
                  </div>

                  <div className="ct-summary-box">
                    <label>Edited</label>
                    <b>
                      {
                        arr(selectedCustomerTarget.monthlyTargets).filter(
                          (month) => month.isEdited || month.isCustomerEdited,
                        ).length
                      }
                    </b>
                  </div>
                </div>

                <div className="ct-note blue">
                  Editing a customer month applies only to this customer and all
                  upcoming editable months. Other customers will not change.
                </div>

                <div className="ct-month-grid">
                  {arr(selectedCustomerTarget.monthlyTargets).map(
                    (monthRow) => {
                      const completed = isMonthCompleted(fy, monthRow.month);
                      const active =
                        norm(monthView?.monthRow?.month) ===
                        norm(monthRow.month);

                      return (
                        <button
                          type="button"
                          key={monthRow.month}
                          className={`ct-month-card ${
                            monthRow.isEdited || monthRow.isCustomerEdited
                              ? "edited"
                              : ""
                          } ${completed ? "completed" : ""} ${
                            active ? "selected" : ""
                          }`}
                          onClick={() =>
                            openMonthView(selectedCustomerTarget, monthRow)
                          }
                        >
                          <div>
                            <div className="ct-month-name">
                              {monthRow.month}
                            </div>

                            <div className="ct-muted">
                              {completed ? "Locked view" : "Click to view/edit"}
                            </div>
                          </div>

                          <div className="ct-month-total">
                            {money(monthRow.targetTotal)}
                          </div>

                          <div className="ct-month-meta">
                            <span>
                              {getMonthProducts(monthRow).length} Products
                            </span>

                            <span
                              className={`ct-chip ${
                                completed
                                  ? "gray"
                                  : monthRow.isEdited ||
                                      monthRow.isCustomerEdited
                                    ? "green"
                                    : ""
                              }`}
                            >
                              {completed
                                ? "Locked"
                                : monthRow.isEdited || monthRow.isCustomerEdited
                                  ? "Updated"
                                  : "Editable"}
                            </span>
                          </div>
                        </button>
                      );
                    },
                  )}
                </div>

                {monthView ? (
                  <div className="ct-month-view">
                    <div className="ct-section-head">
                      <div>
                        <h3 className="ct-view-title">
                          {monthView.monthRow.month} Target -{" "}
                          {selectedCustomerTarget.customerName}
                        </h3>

                        <p className="ct-view-sub">
                          Month Total: {money(monthView.monthRow.targetTotal)} •
                          Products: {selectedMonthProducts.length}
                        </p>
                      </div>

                      <button
                        type="button"
                        className="ct-btn primary"
                        onClick={() =>
                          openMonthEditor(
                            selectedCustomerTarget,
                            monthView.monthRow,
                          )
                        }
                        disabled={selectedMonthLocked}
                      >
                        {selectedMonthLocked ? "Locked" : "Edit Month"}
                      </button>
                    </div>

                    {renderProductViewTable(selectedMonthProducts)}
                  </div>
                ) : null}
              </>
            )}
          </div>
        </div>
      )}

      {monthEditor ? (
        <div className="ct-modal-backdrop">
          <div className="ct-modal">
            <div className="ct-modal-head">
              <div>
                <h3 className="ct-modal-title">
                  Edit {monthEditor.monthRow.month} Customer Target
                </h3>

                <p className="ct-modal-sub">
                  Customer: {monthEditor.customerTarget.customerName} • Sales
                  Person: {monthEditor.customerTarget.salesPersonName} • FY:{" "}
                  {fy} • Month Total: {money(monthTotal)} • Visible Total:{" "}
                  {money(monthVisibleTotal)}
                </p>
              </div>

              <button
                type="button"
                className="ct-close"
                onClick={() => {
                  setMonthEditor(null);
                  setMonthQtyMap({});
                  resetMonthFilters();
                }}
              >
                ×
              </button>
            </div>

            <div className="ct-edit-filter">
              <div className="ct-field">
                <label className="ct-label">Category</label>

                <select
                  className="ct-select"
                  value={monthCategoryFilter}
                  onChange={(e) => {
                    setMonthCategoryFilter(e.target.value);
                    setMonthSubCategoryFilter(FILTER_ALL);
                    setMonthColorFilter(FILTER_ALL);
                  }}
                >
                  <option value={FILTER_ALL}>All Categories</option>

                  {monthCategoryOptions.map((item) => (
                    <option key={item} value={item}>
                      {item}
                    </option>
                  ))}
                </select>
              </div>

              <div className="ct-field">
                <label className="ct-label">Sub Category</label>

                <select
                  className="ct-select"
                  value={monthSubCategoryFilter}
                  onChange={(e) => {
                    setMonthSubCategoryFilter(e.target.value);
                    setMonthColorFilter(FILTER_ALL);
                  }}
                >
                  <option value={FILTER_ALL}>All Sub Categories</option>

                  {monthSubCategoryOptions.map((item) => (
                    <option key={item} value={item}>
                      {item}
                    </option>
                  ))}
                </select>
              </div>

              <div className="ct-field">
                <label className="ct-label">Color</label>

                <select
                  className="ct-select"
                  value={monthColorFilter}
                  onChange={(e) => setMonthColorFilter(e.target.value)}
                >
                  <option value={FILTER_ALL}>All Colors</option>

                  {monthColorOptions.map((item) => (
                    <option key={item} value={item}>
                      {item}
                    </option>
                  ))}
                </select>
              </div>

              <div className="ct-field">
                <label className="ct-label">Search Product</label>

                <input
                  className="ct-input"
                  value={monthSearch}
                  onChange={(e) => setMonthSearch(e.target.value)}
                  placeholder="Search product / color / HSN"
                />
              </div>

              <div className="ct-field">
                <label className="ct-label">Visible / Total</label>

                <div className="ct-stat">
                  <span>{monthRows.length}</span>
                  <b>{allMonthRows.length}</b>
                </div>
              </div>
            </div>

            <div className="ct-edit-bar">
              <div className="ct-view-sub">
                Add product by entering qty, reduce by lowering qty, remove by
                setting qty to 0.
              </div>

              <div className="ct-view-sub">
                Month Total: <b>{money(monthTotal)}</b>
              </div>
            </div>

            <div className="ct-modal-body">{renderEditProductTable()}</div>

            <div className="ct-modal-foot">
              <div className="ct-view-sub">
                Saving applies only to{" "}
                <b>{monthEditor.customerTarget.customerName}</b> for{" "}
                <b>{monthEditor.monthRow.month}</b> and upcoming editable
                months. Other customers will not change.
              </div>

              <div className="ct-actions">
                <button
                  type="button"
                  className="ct-btn light"
                  onClick={() => {
                    setMonthEditor(null);
                    setMonthQtyMap({});
                    resetMonthFilters();
                  }}
                >
                  Cancel
                </button>

                <button
                  type="button"
                  className="ct-btn primary"
                  onClick={saveCustomerMonthTarget}
                  disabled={savingMonth}
                >
                  {savingMonth ? "Saving..." : "Update Customer Month"}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
